import type { TFunction } from 'i18next';

// Type-only — keeps this module free of any runtime edge into the editor-session component,
// so the 409 modal in lib/handleApiError can share it without dragging React state along.
import type { LockBlockState } from './WorkflowEditorSession';

/**
 * Server-side lock TTL, in minutes — mirrors `WorkflowTemplate.LockTtl` on the BE. Used only to
 * say "released after N minutes of inactivity"; nothing on the FE enforces it. A domain test
 * pins the BE constant to 30 so a change there fails loudly instead of silently drifting from
 * this copy.
 */
export const LOCK_TTL_MINUTES = 30;

export type LockBannerCopy = {
  /** Headline — what happened to the lock. */
  title: string;
  /** Supporting lines, already ordered. May be empty; render each on its own row. */
  lines: string[];
};

/**
 * The single source of copy for every "you can't edit this" surface: the in-page banners on
 * Details / PDF Mapping / Form Builder, and the sticky 409 modal in `handleApiError`.
 *
 * It exists because the two lock outcomes used to read identically. A reclaim and a TTL lapse
 * both produced "Currently being edited by {name}" — and on a lapse the name resolved to the
 * generic fallback, or (via the 409 body) to the reader's own name, telling them a workflow
 * was being edited by someone who wasn't there. Branching in one place keeps the banner and
 * the modal from drifting apart the way they would if each site did its own `t()` chain.
 *
 * Layout stays with the caller — this returns strings only, in the spirit of the sibling
 * {@link file://./lockTooltip.ts} helper.
 */
export function lockBannerCopy(
  block: LockBlockState,
  t: TFunction,
  locale: string,
): LockBannerCopy {
  if (block.reason === 'expired') {
    return {
      title: t('workflow.lock.expiredTitle', { minutes: LOCK_TTL_MINUTES }),
      lines: [t('workflow.lock.expiredBody')],
    };
  }

  const title = t('workflow.lock.takenOverTitle', {
    name: block.holderName ?? t('workflow.lock.anotherUser'),
  });

  // No expiry on the payload means a legacy/partial lock row — say what we can rather than
  // rendering "Lock held until undefined".
  if (!block.expiresAt) {
    return { title, lines: [t('workflow.lock.takenOverBodyUnknown')] };
  }
  return {
    title,
    lines: [
      t('workflow.lock.takenOverBody', {
        time: new Date(block.expiresAt).toLocaleString(locale),
      }),
    ],
  };
}

/**
 * Copy for the Reclaim confirmation, which also has to change with the reason: "the other
 * editor will be blocked from saving" is simply false once the lock has expired, because
 * there is no other editor left to block.
 */
export function reclaimConfirmCopy(
  block: LockBlockState,
  t: TFunction,
): { action: string; title: string; description: string } {
  if (block.reason === 'expired') {
    return {
      action: t('workflow.lock.resumeAction'),
      title: t('workflow.lock.resumeConfirmTitle'),
      description: t('workflow.lock.resumeConfirmDesc'),
    };
  }
  return {
    action: t('workflow.lock.forceReleaseAction'),
    title: t('workflow.lock.forceReleaseConfirmTitle'),
    description: t('workflow.lock.forceReleaseConfirmDesc'),
  };
}
